import { useState, useEffect } from 'react';
import { api } from '../lib/api.js';

// Inline reference card rendered in a chat bubble.
export function ChatRefCard({ ref, mine, currentUser }) {
  const safeRef = (ref && typeof ref === 'object') ? ref : { kind: '_invalid' };
  const [info, setInfo] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => {
    if (safeRef.kind === 'image' || safeRef.kind === '_invalid') return;
    let p = null;
    if (safeRef.kind === 'recipe' && safeRef.id) p = api('GET', `/api/recipes/${safeRef.id}`).then(r => ({ title: r.recipe?.name, sub: r.recipe?.category }));
    else if (safeRef.kind === 'task' && safeRef.id) p = api('GET', `/api/tasks/${safeRef.id}`).then(r => ({ title: r.task?.title, sub: r.task?.time_period }));
    else if (safeRef.kind === 'knowledge' && safeRef.id) p = api('GET', `/api/knowledge-docs/${safeRef.id}`).then(r => ({ title: r.document?.title || r.doc?.title, sub: r.document?.type || r.doc?.type }));
    if (p) p.then(setInfo).catch(() => setInfo({ title: '(unavailable)', sub: '' }));
  }, [safeRef.kind, safeRef.id]);
  // Defense: a malformed ref must never white-screen the conversation.
  // CJ hit this with a partial image upload that left ref.url undefined.
  if (safeRef.kind === '_invalid') return null;
  if (ref.kind === 'image') {
    const url = typeof ref.url === 'string' ? ref.url : '';
    if (!url || imgFailed) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11,
                        padding: '4px 8px', borderRadius: 6,
                        background: mine ? 'rgba(255,255,255,0.12)' : '#f5f5f5',
                        color: mine ? '#fff' : '#9e9e9e' }}>
          📷 (image unavailable)
        </span>
      );
    }
    // Server now embeds an HMAC-signed URL on each image ref (Phase 4 / C5).
    // For older cached messages the field may be missing — fall back to the
    // bare URL, which the kiosk path handles anyway. After the next refresh
    // every visible message has a signed URL.
    const displayUrl = ref.signed_url || url;
    return (
      <>
        <img src={displayUrl} alt={ref.name || 'image'}
          onClick={() => setZoomed(true)}
          onError={() => setImgFailed(true)}
          style={{ maxWidth: 220, maxHeight: 220, borderRadius: 8, cursor: 'zoom-in',
                    border: `1px solid ${mine ? 'rgba(255,255,255,0.25)' : '#cfd8dc'}`,
                    objectFit: 'cover' }} />
        {zoomed && (
          <div onClick={() => setZoomed(false)}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      zIndex: 100, cursor: 'zoom-out', padding: 16 }}>
            <img src={displayUrl} alt={ref.name || 'image'}
              style={{ maxWidth: '95vw', maxHeight: '95vh', borderRadius: 8 }} />
          </div>
        )}
      </>
    );
  }
  const icon = ref.kind === 'recipe' ? '🍵' : ref.kind === 'task' ? '☑' : '📄';
  const onClick = () => {
    // Naive: just navigate via tab. Each ref kind has a dedicated
    // surface; for v1 we just open the URL hash so the SPA router
    // can land closest. (Deeper deeplinks land in C5 / chat search.)
    if (ref.kind === 'recipe') alert(`Recipe #${ref.id}: ${info?.title || ''}`);
    else if (ref.kind === 'task') alert(`Task #${ref.id}: ${info?.title || ''}`);
    else if (ref.kind === 'knowledge') alert(`Doc #${ref.id}: ${info?.title || ''}`);
  };
  return (
    <button onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px',
                background: mine ? 'rgba(255,255,255,0.12)' : '#fff',
                color: mine ? '#fff' : '#212121',
                border: `1px solid ${mine ? 'rgba(255,255,255,0.25)' : '#cfd8dc'}`,
                borderRadius: 8, fontSize: 12, fontFamily: 'inherit', cursor: 'pointer',
                textAlign: 'left' }}>
      <span style={{ fontSize: 16 }}>{icon}</span>
      <div style={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {info?.title || 'Loading…'}
        </span>
        {info?.sub && (
          <span style={{ fontSize: 10, opacity: 0.7 }}>{info.sub}</span>
        )}
      </div>
    </button>
  );
}
